import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import WhatsAppIcon from "./icons/WhatsAppIcon";
import { BUSINESS } from "../lib/business";

const NAV = [
    { label: "Services", href: "#services" },
    { label: "Why Us", href: "#why-us" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Reviews", href: "#reviews" },
    { label: "Contact", href: "#contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 w-full transition-all duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm"
                    : "bg-white/80 backdrop-blur-sm border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <a
                        href="#top"
                        data-testid="header-logo-link"
                        className="flex items-center gap-3 group"
                    >
                        <img
                            src={BUSINESS.logo}
                            alt="Call The Handyman logo"
                            className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
                            data-testid="header-logo-img"
                        />
                        <span className="sr-only">{BUSINESS.name}</span>
                    </a>

                    <nav
                        className="hidden md:flex items-center gap-8"
                        data-testid="desktop-nav"
                    >
                        {NAV.map((item) => (
                            <a
                                key={item.href}
                                href={item.href}
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors relative after:absolute after:bottom-[-6px] after:left-0 after:h-[2px] after:w-0 after:bg-amber-600 after:transition-all hover:after:w-full"
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-3">
                        <a
                            href={BUSINESS.whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-whatsapp-cta"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold text-sm shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
                        >
                            <WhatsAppIcon className="w-4 h-4" />
                            <span>Chat on WhatsApp</span>
                        </a>
                    </div>

                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-slate-900 hover:bg-slate-100"
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle navigation menu"
                    >
                        {open ? (
                            <X className="w-6 h-6" />
                        ) : (
                            <Menu className="w-6 h-6" />
                        )}
                    </button>
                </div>

                {open && (
                    <div
                        className="md:hidden pb-6 pt-2 border-t border-slate-100"
                        data-testid="mobile-nav"
                    >
                        <nav className="flex flex-col gap-1">
                            {NAV.map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="px-3 py-3 rounded-md text-base font-semibold text-slate-800 hover:bg-slate-50"
                                >
                                    {item.label}
                                </a>
                            ))}
                            <a
                                href={BUSINESS.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-whatsapp-cta"
                                className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold shadow-sm"
                            >
                                <WhatsAppIcon className="w-4 h-4" />
                                Chat on WhatsApp
                            </a>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}
